import React, { useEffect, useState } from "react";
import { DataStore } from "aws-amplify";
import {
  AppUser,
  DailyLog,
  DayDiagnoseComment,
  DayDiagnosis,
  Diagnosis,
  DiagnosisSummary,
  Photo,
  PhotoComment,
} from "../../models";
import {
  StyleSheet,
  Text,
  ScrollView,
  TouchableOpacity,
  View,
  SafeAreaView,
  Image,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { DiagnoseType, Category } from "../../models/";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import { googleMapsApiKey } from "../components/AppConst";
import { formatToJapanDate, S3Image, appAlert, makeMultipleLines1Line, sleep } from "../components/CmnUtil";
import { Header } from "../components/Header";
import {
  CommentDialog,
  CommentType,
  PhotoDialog,
  SaveDialog,
} from "../components/CmnDiaLog";
import IconImages from "../components/IconImages";
import Storage from '@aws-amplify/storage';

import { API } from "aws-amplify";
import { GraphQLResult } from "@aws-amplify/api-graphql";
import {
  getAppUser,
  getDayDiagnosis,
  listDailyLogsByFarm,
  listDayDiagnoseCommentsByDayDiagnosis,
  listDiagnosesByPhotoID,
  listDiagnosisSummariesByDayDiagnosis,
  listPhotoCommentsByPhoto,
  listPhotosByDayDiagnosis,
} from "../../graphql/queries";
import {
  GetAppUserQuery,
  GetDayDiagnosisQuery,
  ListDailyLogsByFarmQuery,
  ListDayDiagnoseCommentsByDayDiagnosisQuery,
  ListDiagnosesByPhotoIDQuery,
  ListDiagnosisSummariesByDayDiagnosisQuery,
  ListPhotoCommentsByPhotoQuery,
  ListPhotosByDayDiagnosisQuery,
} from "../../API";

type DayDiagnosisType = {
  farmLatLng: { lat: number | undefined; lng: number | undefined };
  date: string;
  farmName: string;
  photoCount: number;
  avgVegetationCoverage: number | undefined;
  avgBranchLength: number | undefined;
  avgFloweringRate: number | undefined;
  avgBearingNumber: number | undefined;
  avgFruitSize: number | undefined;
  fruitMaxSize: number | undefined;
  fruitMinSize: number | undefined;
  dailyLogs: string[];
  comment: string;
  icon: string;
  commentCount: number;
};

type DiagnosisType = {
  photoId: string;
  photoKey: string;
  takenAt: string;
  vegetationCoverage: number | undefined;
  branchLength: number | undefined;
  floweringRate: number | undefined;
  bearingNumber: number | undefined;
  fruitSize: number | undefined;
  fruitMaxSize: number | undefined;
  fruitMinSize: number | undefined;
  comment: string;
  icon: string;
  commentCount: number;
};

const day = ["日", "月", "火", "水", "木", "金", "土"];

/** ================================
 *  撮影画像一覧画面
 *  ================================*/
const DiagnosisPhotoList = ({
  navigation,
  route,
}: {
  navigation: any;
  route: any;
}) => {
  // ユーザー情報を取得
  async function queryAppUserById(id: string) {
    const res = (await API.graphql({
      query: getAppUser,
      variables: {
        id,
      },
    })) as GraphQLResult<GetAppUserQuery>;
    if (res.errors) {
      throw res.errors;
    }
    if (res.data && res.data.getAppUser) {
      const user = res.data.getAppUser;
      return user as unknown as AppUser; // 実質同じだが定義元が異なるので強制キャスト
    }
    return null;
  }

  // 日別AI診断結果を取得
  async function queryDayDiagnosisById(id: string) {
    const res = (await API.graphql({
      query: getDayDiagnosis,
      variables: {
        id,
      },
    })) as GraphQLResult<GetDayDiagnosisQuery>;
    if (res.errors) {
      throw res.errors;
    }
    if (res.data && res.data.getDayDiagnosis) {
      const dayDiagnosis = res.data.getDayDiagnosis;
      return dayDiagnosis as unknown as DayDiagnosis; // 実質同じだが定義元が異なるので強制キャスト
    }
    return null;
  }
  // 撮影画像を取得
  async function queryPhotosByDayDiagnosis(dayDiagnosisID: string) {
    const res = (await API.graphql({
      query: listPhotosByDayDiagnosis,
      variables: {
        dayDiagnosisID,
        sortDirection: "DESC",
        limit: 1000,
      },
    })) as GraphQLResult<ListPhotosByDayDiagnosisQuery>;
    if (res.errors) {
      throw res.errors;
    }
    if (res.data && res.data.listPhotosByDayDiagnosis) {
      const items = res.data.listPhotosByDayDiagnosis.items.filter(c => !c?._deleted);
      return items as unknown as Photo[]; // 実質同じだが定義元が異なるので強制キャスト
    }
    return [];
  }

  // 日次集計用AI診断結果を取得
  async function queryDiagnosisSummariesByDayDiagnosis(dayDiagnosisID: string) {
    const res = (await API.graphql({
      query: listDiagnosisSummariesByDayDiagnosis,
      variables: {
        dayDiagnosisID,
        limit: 10000
      },
    })) as GraphQLResult<ListDiagnosisSummariesByDayDiagnosisQuery>;
    if (res.errors) {
      throw res.errors;
    }
    if (res.data && res.data.listDiagnosisSummariesByDayDiagnosis) {
      const items = res.data.listDiagnosisSummariesByDayDiagnosis.items.filter(c => !c?._deleted);
      return items as unknown as DiagnosisSummary[]; // 実質同じだが定義元が異なるので強制キャスト
    }
    return [];
  }

  // 作業日誌を取得
  async function queryDailyLogsByFarmAndDate(farmID: string, date: string) {
    const res = (await API.graphql({
      query: listDailyLogsByFarm,
      variables: {
        farmID,
        // dateCategory: {eq: {date}} こうしたいがフォーマットエラーになる。未解決
        filter: { date: { eq: date } },
        limit: 2000,
      },
    })) as GraphQLResult<ListDailyLogsByFarmQuery>;
    if (res.errors) {
      throw res.errors;
    }
    if (res.data && res.data.listDailyLogsByFarm) {
      const items = res.data.listDailyLogsByFarm.items.filter(c => !c?._deleted);
      return items as unknown as DailyLog[]; // 実質同じだが定義元が異なるので強制キャスト
    }
    return [];
  }

  // 日別診断結果コメントを取得
  async function queryDayDiagnoseCommentsByDayDiagnosis(
    dayDiagnosisID: string
  ) {
    const res = (await API.graphql({
      query: listDayDiagnoseCommentsByDayDiagnosis,
      variables: {
        dayDiagnosisID,
        sortDirection: "DESC",
      },
    })) as GraphQLResult<ListDayDiagnoseCommentsByDayDiagnosisQuery>;
    if (res.errors) {
      throw res.errors;
    }
    if (res.data && res.data.listDayDiagnoseCommentsByDayDiagnosis) {
      // TODO: _deletedの除外はこうするしかないのか？
      const items = res.data.listDayDiagnoseCommentsByDayDiagnosis.items.filter(c => !c?._deleted);
      return items as unknown as DayDiagnoseComment[]; // 実質同じだが定義元が異なるので強制キャスト
    }
    return [];
  }

  // AI診断結果を取得
  async function queryDiagnosesByPhotoId(photoID: string) {
    const res = (await API.graphql({
      query: listDiagnosesByPhotoID,
      variables: {
        photoID,
        limit: 10000
      },
    })) as GraphQLResult<ListDiagnosesByPhotoIDQuery>;
    if (res.errors) {
      throw res.errors;
    }
    if (res.data && res.data.listDiagnosesByPhotoID) {
      const items = res.data.listDiagnosesByPhotoID.items.filter(c => !c?._deleted);
      return items as unknown as Diagnosis[]; // 実質同じだが定義元が異なるので強制キャスト
    }
    return [];
  }

  // 撮影画像コメントを取得
  async function queryPhotoCommentsByPhoto(photoID: string) {
    const res = (await API.graphql({
      query: listPhotoCommentsByPhoto,
      variables: {
        photoID,
        sortDirection: "DESC",
      },
    })) as GraphQLResult<ListPhotoCommentsByPhotoQuery>;
    if (res.errors) {
      throw res.errors;
    }
    if (res.data && res.data.listPhotoCommentsByPhoto) {
      // TODO: _deletedの除外はこうするしかないのか？
      const items = res.data.listPhotoCommentsByPhoto.items.filter(c => !c?._deleted);
      return items as unknown as PhotoComment[]; // 実質同じだが定義元が異なるので強制キャスト
    }
    return [];
  }

  // 日別AI診断結果のデータを格納するState
  const [dayDiagnosisData, setDayDiagnosisData] = useState<DayDiagnosisType>({
    farmLatLng: { lat: undefined, lng: undefined },
    date: "",
    farmName: "",
    photoCount: 0,
    avgVegetationCoverage: undefined,
    avgBranchLength: undefined,
    avgFloweringRate: undefined,
    avgBearingNumber: undefined,
    avgFruitSize: undefined,
    fruitMaxSize: undefined,
    fruitMinSize: undefined,
    dailyLogs: [],
    comment: "",
    icon: "",
    commentCount: 0,
  });

  //日別AI診断結果に紐づく撮影画像のデータを格納
  const [photos, setPhotos] = useState<Photo[]>([]);

  // 地図上の撮影位置ポイント
  const [pins, setPins] = useState<{ text: string, lat: number, lng: number }[]>([]);

  // 各AI診断結果のデータを格納するState
  const [diagnosisData, setDiagnosisData] = useState<DiagnosisType[]>([]);

  // 診断結果のidを格納する
  const DAY_DIAGNOSIS_ID = route.params.id;

  // コメント送信先のidを格納するState
  const [destinationId, setDestinationId] = useState<string>(DAY_DIAGNOSIS_ID);

  // コメントダイアログフラグを格納するState
  const [commentDialogFlag, setCommentDialogFlag] = useState<boolean>(false);

  // コメント対象を格納するState
  const [type, setType] = useState<CommentType>("dayDiagnosis");

  // googleMap
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: googleMapsApiKey,
  });

  // 撮影画像の拡大
  const [enlargePhotoKey, setEnlargePhotoKey] = useState<string>("");
  const [photoDialogFlag, setPhotoDialogFlag] = useState<boolean>(false);

  // 削除ダイアログ表示フラグ
  const [delPhoto, setDelPhoto] = useState<{
    photoId: string;
    photoKey: string;
  } | undefined>(undefined);

  // 日別AI診断結果を取得
  async function queryDayDiagnosis() {
    // 送られてきたidから日別AI診断結果を取得
    const dayDiagnosis = await queryDayDiagnosisById(route.params.id);

    // 追加するデータ
    let addData: DayDiagnosisType = {
      farmLatLng: { lat: 0, lng: 0 },
      date: "",
      farmName: "",
      photoCount: 0,
      avgVegetationCoverage: undefined,
      avgBranchLength: undefined,
      avgFloweringRate: undefined,
      avgBearingNumber: undefined,
      avgFruitSize: undefined,
      fruitMaxSize: undefined,
      fruitMinSize: undefined,
      dailyLogs: [],
      comment: "",
      icon: "",
      commentCount: 0,
    };

    // 圃場の緯度経度を取得&格納
    const farmLatLng = {
      lat: dayDiagnosis ? dayDiagnosis.farm.lat : 0,
      lng: dayDiagnosis ? dayDiagnosis.farm.lng : 0,
    };
    addData.farmLatLng = farmLatLng;

    // 日付を取得&フォーマット変更&格納
    const date = dayDiagnosis ? dayDiagnosis.date : "";
    addData.date = formatToJapanDate(new Date(date));

    // 圃場の名前を取得&格納
    const farmName = dayDiagnosis ? dayDiagnosis.farm.name : "";
    addData.farmName = farmName;

    // 日別AI診断結果に紐づく撮影画像を取得
    const photos: Photo[] = await queryPhotosByDayDiagnosis(DAY_DIAGNOSIS_ID);

    // 撮影画像をphotosに格納
    setPhotos(photos);

    // 撮影画像の枚数を取得&格納
    addData.photoCount = photos.length;

    // 日別AI診断結果に紐づく日次集計用AI診断結果を取得
    const diagnosisSummary: DiagnosisSummary[] =
      await queryDiagnosisSummariesByDayDiagnosis(DAY_DIAGNOSIS_ID);

    // 植被率平均を取得&格納
    const avgVegetationCoverage = diagnosisSummary.filter(
      (c) => c.diagnoseType == DiagnoseType.VEGETATION_COVERAGE
    );
    if (avgVegetationCoverage.length != 0) {
      addData.avgVegetationCoverage = avgVegetationCoverage[0].diagnoseResult;
    }

    // 枝の長さ平均を取得&格納
    const avgBranchLength = diagnosisSummary.filter(
      (c) => c.diagnoseType == DiagnoseType.BRANCH_LENGTH
    );
    if (avgBranchLength.length != 0) {
      addData.avgBranchLength = avgBranchLength[0].diagnoseResult;
    }

    // 開花率平均を取得&格納
    const avgFloweringRate = diagnosisSummary.filter(
      (c) => c.diagnoseType == DiagnoseType.FLOWERING_RATE
    );
    if (avgFloweringRate.length != 0) {
      addData.avgFloweringRate = avgFloweringRate[0].diagnoseResult;
    }

    // 着果数平均を取得&格納
    const avgBearingNumber = diagnosisSummary.filter(
      (c) => c.diagnoseType == DiagnoseType.BEARING_NUMBER
    );
    if (avgBearingNumber.length != 0) {
      addData.avgBearingNumber = avgBearingNumber[0].diagnoseResult;
    }

    // 果実サイズ平均を取得&格納
    const avgFruitSize = diagnosisSummary.filter(
      (c) => c.diagnoseType == DiagnoseType.FRUIT_SIZE
    );
    if (avgFruitSize.length != 0) {
      addData.avgFruitSize = avgFruitSize[0].diagnoseResult;
    }

    // 最大果実サイズを取得&格納
    const fruitMaxSize = diagnosisSummary.filter(
      (c) => c.diagnoseType == DiagnoseType.FRUIT_MAX_SIZE
    );
    if (fruitMaxSize.length != 0) {
      addData.fruitMaxSize = fruitMaxSize[0].diagnoseResult;
    }

    // 最小果実サイズを取得&格納
    const fruitMinSize = diagnosisSummary.filter(
      (c) => c.diagnoseType == DiagnoseType.FRUIT_MIN_SIZE
    );
    if (fruitMinSize.length != 0) {
      addData.fruitMinSize = fruitMinSize[0].diagnoseResult;
    }

    // 圃場に紐づく作業日誌を取得
    if (dayDiagnosis) {
      const dailyLog = await queryDailyLogsByFarmAndDate(
        dayDiagnosis.farm.id,
        date
      );
      // 英語→日本語変換&格納
      const dailyLogs = dailyLog.map((c) => {
        switch (c.category) {
          case Category.FRUITS_THINNING:
            return "摘果";
          case Category.PESTICIDE_SPRAYNING:
            return "農薬散布";
          case Category.MATING:
            return "交配";
          case Category.HARVEST:
            return "収穫";
          case Category.FLOWERING:
            return "開花";
          case Category.OTHERS:
            return c.categoryOptional || "その他";
          default:
            return "その他";
        }
      });
      addData.dailyLogs = dailyLogs;
    }

    // コメントを取得&格納
    const dayDiagnoseComment: DayDiagnoseComment[] =
      await queryDayDiagnoseCommentsByDayDiagnosis(DAY_DIAGNOSIS_ID);

    if (dayDiagnoseComment.length != 0) {
      addData.comment = dayDiagnoseComment[0].text;
    }

    // コメントユーザーのアイコンを取得&格納
    if (dayDiagnoseComment.length != 0) {
      const commentUser = await queryAppUserById(
        dayDiagnoseComment[0].authorID
      );
      if (!!commentUser && !!commentUser.icon) {
        addData.icon = commentUser.icon;
      }
    }

    addData.commentCount = dayDiagnoseComment.length;

    setDayDiagnosisData(addData);
  }

  // ↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓useEffect↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓
  // 送られてきたidからdayDiagnosisに紐づくデータを取得する
  useEffect(() => {
    queryDayDiagnosis();
  }, [route]);

  // 撮影画像に紐づく診断結果のデータを格納
  useEffect(() => {
    (async () => {
      const addData: Promise<DiagnosisType[]> = Promise.all(
        photos.map(async (photo) => {
          // 撮影日時を取得しフォーマットを変更
          const d = new Date(photo.takenAt);
          const takenAt =
            d.getMonth() +
            1 +
            "月" +
            d.getDate() +
            "日(" +
            day[d.getDay()] +
            ") " +
            d.getHours() +
            ":" +
            ("00" + d.getMinutes()).slice(-2);

          // 撮影画像に紐づく診断結果を取得
          const diagnosis: Diagnosis[] = await queryDiagnosesByPhotoId(
            photo.id
          );

          // 植被率を取得
          let vegetationCoverage: number | undefined = undefined;
          const vegetationCoverageData = diagnosis.filter(
            (c) => c.diagnoseType == DiagnoseType.VEGETATION_COVERAGE
          );
          if (vegetationCoverageData.length != 0) {
            vegetationCoverage = vegetationCoverageData[0].diagnoseResult;
          }

          // 枝の長さを取得
          let branchLength: number | undefined = undefined;
          const branchLengthData = diagnosis.filter(
            (c) => c.diagnoseType == DiagnoseType.BRANCH_LENGTH
          );
          if (branchLengthData.length != 0) {
            branchLength = branchLengthData[0].diagnoseResult;
          }

          // 開花率を取得
          let floweringRate: number | undefined = undefined;
          const floweringRateData = diagnosis.filter(
            (c) => c.diagnoseType == DiagnoseType.FLOWERING_RATE
          );
          if (floweringRateData.length != 0) {
            floweringRate = floweringRateData[0].diagnoseResult;
          }

          // 着果数を取得
          let bearingNumber: number | undefined = undefined;
          const bearingNumberData = diagnosis.filter(
            (c) => c.diagnoseType == DiagnoseType.BEARING_NUMBER
          );
          if (bearingNumberData.length != 0) {
            bearingNumber = bearingNumberData[0].diagnoseResult;
          }

          // 果実サイズ平均を取得
          let fruitSize: number | undefined = undefined;
          const fruitSizeData = diagnosis.filter(
            (c) => c.diagnoseType == DiagnoseType.FRUIT_SIZE
          );
          if (fruitSizeData.length != 0) {
            fruitSize = fruitSizeData[0].diagnoseResult;
          }

          // 最大果実サイズを取得
          let fruitMaxSize: number | undefined = undefined;
          const fruitMaxSizeData = diagnosis.filter(
            (c) => c.diagnoseType == DiagnoseType.FRUIT_MAX_SIZE
          );
          if (fruitMaxSizeData.length != 0) {
            fruitMaxSize = fruitMaxSizeData[0].diagnoseResult;
          }

          // 最小果実サイズを取得
          let fruitMinSize: number | undefined = undefined;
          const fruitMinSizeData = diagnosis.filter(
            (c) => c.diagnoseType == DiagnoseType.FRUIT_MIN_SIZE
          );
          if (fruitMinSizeData.length != 0) {
            fruitMinSize = fruitMinSizeData[0].diagnoseResult;
          }

          // 撮影画像に紐づくコメントを取得
          let comment: string = "";
          const comments: PhotoComment[] = await queryPhotoCommentsByPhoto(
            photo.id
          );

          let icon: string = "";

          // コメントがあるとき
          if (comments.length != 0) {
            // コメントに紐づくユーザーのアイコンを取得
            const commentUser = await queryAppUserById(comments[0].authorID);
            comment = comments[0].text;
            if (!!commentUser && !!commentUser.icon) {
              icon = commentUser.icon;
            }
          }

          return {
            photoId: photo.id,
            photoKey: photo.photoKey,
            takenAt: takenAt,
            vegetationCoverage: vegetationCoverage,
            branchLength: branchLength,
            floweringRate: floweringRate,
            bearingNumber: bearingNumber,
            fruitSize: fruitSize,
            fruitMaxSize: fruitMaxSize,
            fruitMinSize: fruitMinSize,
            comment: comment,
            icon: icon,
            commentCount: comments.length,
          };
        })
      );

      setDiagnosisData(await addData);
    })();

    // 撮影位置設定
    const pins = photos.map((p, i, ar) => ({
      text: `${ar.length - i}`,
      lat: p.lat || 0,
      lng: p.lng || 0
    }))
    setPins(pins)
  }, [photos]);

  // 購読
  useEffect(() => {
    // 撮影画像に対するコメントの購読
    const photoSubscribe = DataStore.observe(PhotoComment).subscribe(
      (value) => {
        const tmpValue = value;
        if (tmpValue.opType != "INSERT" && tmpValue.opType != "DELETE") {
          return;
        }
        let changeData = diagnosisData;
        changeData.forEach(async (diagnosis, key) => {
          if (diagnosis.photoId === destinationId) {
            const currentComments = await queryPhotoCommentsByPhoto(diagnosis.photoId)
            // コメント件数を１増やす
            changeData[key].commentCount = currentComments.length;
            if (currentComments.length > 0) {
              // コメントを更新
              changeData[key].comment = tmpValue.element.text;
              // アイコンがあれば
              const appUser = await queryAppUserById(tmpValue.element.authorID);
              changeData[key].icon =
                !!appUser && !!appUser.icon ? appUser.icon : "";
            } else {
              changeData[key].comment = ""
              changeData[key].icon = ""
            }
          }
        });
        setDiagnosisData(changeData);
      }
    );

    // 日別AI診断結果に対するコメントの購読
    const dayDiagnosisSubscribe = DataStore.observe(
      DayDiagnoseComment
    ).subscribe(async (value) => {
      const tmpValue = value;
      if (tmpValue.opType !== "INSERT" && tmpValue.opType !== "DELETE") {
        return;
      }
      let changeData = dayDiagnosisData;

      // コメント件数を更新
      // TODO: DELETEのとき3回実行される。複数回observeしてる？？
      const currentComments = await queryDayDiagnoseCommentsByDayDiagnosis(DAY_DIAGNOSIS_ID);
      changeData.commentCount = currentComments.length
      if (currentComments.length > 0) {
        // コメントを更新
        changeData.comment = currentComments[0].text;
        // アイコンがあれば表示する
        const appUser = await queryAppUserById(currentComments[0].authorID);
        changeData.icon = !!appUser && !!appUser.icon ? appUser.icon : "";
      } else {
        changeData.comment = ""
        changeData.icon = ""
      }
      setDayDiagnosisData(changeData);
    });

    return () => {
      photoSubscribe.unsubscribe();
      dayDiagnosisSubscribe.unsubscribe();
    };
  }, [commentDialogFlag]);

  // ↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓クリック処理↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓
  /**
   * 撮影画像の吹き出しをクリック
   * @param photoId
   */
  const onClickPhotoComment = (photoId: string) => {
    setDestinationId(photoId);
    setCommentDialogFlag(true);
    setType("photo");
  };

  /**
   * 診断結果詳細の吹き出しをクリック
   */
  const onClickDayDiagnosisComment = () => {
    setDestinationId(DAY_DIAGNOSIS_ID);
    setCommentDialogFlag(true);
    setType("dayDiagnosis");
  };

  /**
   * コメントダイアログの「☓」ボタンをクリック
   */
  const onClickClose = () => {
    setCommentDialogFlag(false);
  };

  /**
   * 写真の削除ボタンをクリック
   */
  const deletePhoto = async () => {
    // 写真の削除
    try {
      // DataStore削除
      const comments: PhotoComment[] = await queryPhotoCommentsByPhoto(
        delPhoto!.photoId
      );
      for (const comment of comments) {
        await DataStore.delete(PhotoComment, c => c.id('eq', comment.id));
      }

      const diagnosisList: Diagnosis[] = await queryDiagnosesByPhotoId(
        delPhoto!.photoId
      );
      for (const diagnosis of diagnosisList) {
        await DataStore.delete(Diagnosis, c => c.id('eq', diagnosis.id));
      }

      await DataStore.delete(Photo, c => c.id('eq', delPhoto!.photoId));
      // S3削除
      await Storage.remove(delPhoto!.photoKey);

    } catch (e) {
      console.error(e);
      appAlert('画像の削除に失敗しました。');
    }

    // リロード
    setDelPhoto(undefined);
    await sleep(1000);
    queryDayDiagnosis();
  };

  // ↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓補助コンポーネント↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓
  // 診断結果コンポーネント
  const Diagnose = ({
    diagnosisType,
    index,
  }: {
    diagnosisType: DiagnosisType,
    index: number
  }) => {
    const bgColor = diagnosisType.comment == "" ? "#fff" : "#FFEBF8";
    function getThumbKey(srcKey: string) {
      const lastDot = srcKey.lastIndexOf('.')
      const namePart = srcKey.slice(0, lastDot)
      const ext = srcKey.slice(lastDot + 1)
      return `${namePart}_thumb.${ext}`
    }
    return (
      <View style={[styles.diagnosis, { backgroundColor: bgColor }]}>
        <View style={styles.photoContainer}>
          <TouchableOpacity
            onPress={() => {
              setEnlargePhotoKey(diagnosisType.photoKey.replace('_thumb.', '.').replace('_AR.', '.'));
              setPhotoDialogFlag(true);
            }}
          >
            <S3Image
              resizeMode="cover"
              imageKey={getThumbKey(diagnosisType.photoKey)}
              style={styles.photo}
            />
          </TouchableOpacity>
          {/* 削除機能を一旦マスク */}
          <TouchableOpacity
            onPress={() => {
              setDelPhoto({ photoId: diagnosisType.photoId, photoKey: diagnosisType.photoKey });
            }}
          >
            <Image resizeMode="contain" source={IconImages.icDelete} style={styles.icDelete} />
          </TouchableOpacity>
        </View>
        <View style={styles.detailBox}>
          <View>
            <Text style={styles.photoCounter}>
              ({diagnosisData.length - index}/{diagnosisData.length})
            </Text>
            <Text style={styles.photoDateTime}>{diagnosisType.takenAt}</Text>
            {diagnosisType.vegetationCoverage != undefined && <Text style={styles.indivisualValue}>
              植被率&#65306;{Number((diagnosisType.vegetationCoverage * 100).toFixed(2))}&#037;
            </Text>}
            {diagnosisType.branchLength != undefined && <Text style={styles.indivisualValue}>
              枝の長さ&#65306;{Number(diagnosisType.branchLength.toFixed(2))}m
            </Text>}
            {/* {diagnosisType.floweringRate != undefined && <Text style={styles.indivisualValue}>
              開花率&#65306;{Number((diagnosisType.floweringRate * 100).toFixed(2))}&#037;
            </Text>} */}
            {diagnosisType.bearingNumber != undefined && <Text style={styles.indivisualValue}>
              着果数&#65306;{Number(diagnosisType.bearingNumber.toFixed(2))}個
            </Text>}
            {diagnosisType.fruitSize != undefined && <Text style={styles.indivisualValue}>
              果実サイズ平均&#65306;{Number(diagnosisType.fruitSize.toFixed(2))}cm
            </Text>}
            {diagnosisType.fruitMaxSize != undefined && <Text style={styles.indivisualValue}>
              最大果実サイズ&#65306;{Number(diagnosisType.fruitMaxSize.toFixed(2))}cm
            </Text>}
            {diagnosisType.fruitMinSize != undefined && <Text style={styles.indivisualValue}>
              最小果実サイズ&#65306;{Number(diagnosisType.fruitMinSize.toFixed(2))}cm
            </Text>}
          </View>
          <TouchableOpacity
            style={styles.photoCommentContainer}
            onPress={() => onClickPhotoComment(diagnosisType.photoId)}
          >
            <View style={styles.photoCommentWrapper}>
              {
                diagnosisType.icon == "" ? (
                  <View></View>
                ) : (
                  <S3Image
                    resizeMode="contain"
                    imageKey={diagnosisType.icon}
                    style={styles.photoUserIcon}
                    defaultSource={IconImages.icUser}
                  />
                )
              }
              <Text
                style={styles.photoComment}
                numberOfLines={1}
                ellipsizeMode={"tail"}
              >
                {makeMultipleLines1Line(diagnosisType.comment)}
              </Text>
            </View>
            <View style={styles.createPhotoComment}>
              <MaterialCommunityIcons
                name={"chat-processing-outline"}
                size={28}
              />
              <Text>{diagnosisType.commentCount}件</Text>
            </View>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  // 診断結果一覧コンポーネント
  const Diagnosises = ({ }) => {
    const diagnoseElements = diagnosisData.map((photo, key) => {
      return (
        <Diagnose
          diagnosisType={photo}
          index={key}
          key={key}
        />
      );
    });

    return <View style={styles.diagnosisContainer}>{diagnoseElements}</View>;
  };

  return (
    <SafeAreaView style={styles.container}>
      <Header onPress={() => navigation.goBack()} goBackText="＜ 一覧へ" />

      <CommentDialog
        onClickClose={() => onClickClose()}
        id={destinationId}
        type={type}
        visible={commentDialogFlag}
      />

      <ScrollView style={styles.container}>
        <View style={styles.wrapper}>
          <View style={styles.dayDiagnosisContainer}>
            {loadError ? (
              <Text>現在、地図を読み込むことができません。</Text>
            ) : isLoaded &&
              dayDiagnosisData.farmLatLng.lat &&
              dayDiagnosisData.farmLatLng.lng ? (
              <GoogleMap
                mapContainerStyle={{
                  width: "100%",
                  height: "160px",
                  marginBottom: "5px",
                }}
                center={{
                  lat: dayDiagnosisData.farmLatLng.lat,
                  lng: dayDiagnosisData.farmLatLng.lng,
                }}
                zoom={18}
                options={{
                  zoomControl: false,
                  mapTypeControl: false,
                  scaleControl: false,
                  streetViewControl: false,
                  rotateControl: false,
                  fullscreenControl: false,
                  mapTypeId: "hybrid",
                  //gestureHandling: "none",
                  keyboardShortcuts: false,
                }}
              >
                <Marker
                  position={{
                    lat: dayDiagnosisData.farmLatLng.lat,
                    lng: dayDiagnosisData.farmLatLng.lng,
                  }}
                  zIndex={10}
                  key={-1}
                />
                {pins.map((pin, i) => <Marker
                  position={{ lat: pin.lat, lng: pin.lng, }}
                  label={{ text: pin.text }}
                  icon={{
                    fillColor: "#FFFF00",                //塗り潰し色
                    fillOpacity: 1.0,                    //塗り潰し透過率
                    path: google.maps.SymbolPath.CIRCLE, //円を指定
                    scale: 12,                           //円のサイズ
                    strokeColor: "#FFFF00",              //枠の色
                    strokeWeight: 1.0                    //枠の透過率
                  }}
                  zIndex={20}
                  key={i}
                />)}
              </GoogleMap>
            ) : (
              <Text>loading...</Text>
            )}
            <View style={styles.dayDiagnosisWrapper}>
              <View style={styles.dayDiagnosisData}>
                <Text style={styles.dayDiagnosisDate}>
                  {dayDiagnosisData.date}
                </Text>
                <Text style={styles.farmName}>{dayDiagnosisData.farmName}</Text>
                <Text style={styles.summaryValue}>
                  撮影枚数&#65306;{dayDiagnosisData.photoCount}枚
                </Text>
                {dayDiagnosisData.avgVegetationCoverage != undefined && <Text style={styles.summaryValue}>
                  植被率平均&#65306;
                  {Number(
                    (dayDiagnosisData.avgVegetationCoverage * 100).toFixed(2)
                  )}
                  &#037;
                </Text>}
                {dayDiagnosisData.avgBranchLength != undefined && <Text style={styles.summaryValue}>
                  枝の長さ平均&#65306;
                  {Number(dayDiagnosisData.avgBranchLength.toFixed(2))}m
                </Text>}
                {dayDiagnosisData.avgFloweringRate != undefined && <Text style={styles.summaryValue}>
                  開花率平均&#65306;
                  {Number((dayDiagnosisData.avgFloweringRate * 100).toFixed(2))}&#037;
                </Text>}
                {dayDiagnosisData.avgBearingNumber != undefined && <Text style={styles.summaryValue}>
                  着果数平均&#65306;
                  {Number(dayDiagnosisData.avgBearingNumber.toFixed(2))}個
                </Text>}
                {dayDiagnosisData.avgFruitSize != undefined && <Text style={styles.summaryValue}>
                  果実サイズ平均&#65306;
                  {Number(dayDiagnosisData.avgFruitSize.toFixed(2))}cm
                </Text>}
                {dayDiagnosisData.fruitMaxSize != undefined && <Text style={styles.summaryValue}>
                  最大果実サイズ&#65306;
                  {Number(dayDiagnosisData.fruitMaxSize.toFixed(2))}cm
                </Text>}
                {dayDiagnosisData.fruitMinSize != undefined && <Text style={styles.summaryValue}>
                  最小果実サイズ&#65306;
                  {Number(dayDiagnosisData.fruitMinSize.toFixed(2))}cm
                </Text>}
                <Text style={styles.dailyLogs}>
                  作業日誌&#65306;{dayDiagnosisData.dailyLogs}
                </Text>
              </View>
              <TouchableOpacity
                style={styles.dayDiagnosisCommentContaienr}
                onPress={() => onClickDayDiagnosisComment()}
              >
                {dayDiagnosisData.icon == "" ? (
                  <View style={styles.dayDiagnosisUserNone}></View>
                ) : (
                  <S3Image
                    resizeMode="contain"
                    imageKey={dayDiagnosisData.icon}
                    style={styles.dayDiagnosisUserIcon}
                    defaultSource={IconImages.icUser}
                  />
                )}
                <Text
                  style={styles.dayDiagnosisComment}
                  numberOfLines={2}
                  ellipsizeMode={"tail"}
                >
                  {dayDiagnosisData.comment}
                </Text>
                <View style={styles.createDayDiagnosisComment}>
                  <MaterialCommunityIcons
                    name={"chat-processing-outline"}
                    size={28}
                  />
                  <Text>{dayDiagnosisData.commentCount}件</Text>
                </View>
              </TouchableOpacity>
            </View>
          </View>

          <Diagnosises />
        </View>
      </ScrollView>

      <PhotoDialog
        visible={photoDialogFlag}
        imageKey={enlargePhotoKey}
        onClickScreen={() => setPhotoDialogFlag(false)}
      />

      { /* 削除ダイアログ */}
      <SaveDialog
        cancelButtonName="キャンセル"
        saveButtonName={"削除"}
        message={"画像を削除しますか？"}
        visible={delPhoto != undefined}
        canceAction={() => { setDelPhoto(undefined); }}
        saveAction={() => { deletePhoto(); }}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#E2F0D9",
  },
  wrapper: {
    width: "90%",
    justifyContent: "center",
    marginHorizontal: "auto",
  },
  dayDiagnosisContainer: {
    width: "100%",
    backgroundColor: "#ffffff",
    padding: 5,
    shadowColor: "#808080",
    shadowOffset: { width: 5, height: 5 },
    shadowRadius: 10,
    marginBottom: 20,
  },
  dayDiagnosisWrapper: {
    flexDirection: "row",
  },
  dayDiagnosisData: {
    flex: 0.6,
    paddingLeft: 10,
  },
  dayDiagnosisDate: {
    fontSize: 18,
    fontWeight: "bold",
  },
  farmName: {
    fontSize: 18,
    fontWeight: "bold",
  },
  summaryValue: {
    fontSize: 15,
  },
  dailyLogs: {
    fontSize: 15,
  },
  dayDiagnosisCommentContaienr: {
    flex: 0.4,
    borderColor: "#507E32",
    borderStyle: "solid",
    borderLeftWidth: 2,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    paddingLeft: 5,
  },
  dayDiagnosisUserNone: {
    height: 30,
  },
  dayDiagnosisUserIcon: {
    width: 30,
    height: 30,
    borderRadius: 15,
    marginRight: 10,
    backgroundColor: "#808080",
  },
  dayDiagnosisComment: {
    fontSize: 15,
  },
  createDayDiagnosisComment: {
    position: "absolute",
    right: 5,
    bottom: 5,
    alignItems: "center",
  },
  diagnosisContainer: {
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  diagnosis: {
    width: "100%",
    padding: 5,
    flexDirection: "row",
    shadowColor: "#808080",
    shadowOffset: { width: 5, height: 5 },
    shadowRadius: 10,
    marginBottom: 10,
  },
  photoContainer: {
    flex: 0.4,
    justifyContent: "space-between",
    marginRight: 10,
  },
  photo: {
    width: "100%",
    height: 170,
    backgroundColor: "#808080",
  },
  icDelete: {
    height: 30,
    width: 30,
  },
  detailBox: {
    flex: 0.6,
    justifyContent: "space-between",
  },
  photoCounter: {
    fontSize: 15,
    textAlign: "right",
  },
  photoDateTime: {
    fontSize: 15,
    fontWeight: "bold",
    textAlign: "center",
  },
  indivisualValue: {
    fontSize: 15,
  },
  photoCommentContainer: {
    borderColor: "#507E32",
    borderStyle: "solid",
    borderTopWidth: 2,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    paddingTop: 5,
  },
  photoCommentWrapper: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
    height: 30,
  },
  photoUserIcon: {
    width: 30,
    height: 30,
    borderRadius: 15,
    marginRight: 10,
    backgroundColor: "#808080",
  },
  photoComment: {
    fontSize: 15,
    maxWidth: "70%",
  },
  createPhotoComment: {
    position: "absolute",
    right: 0,
    alignItems: "center",
  },
});

export default DiagnosisPhotoList;
